// libs
import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
// components
import { IconButton, Tooltip } from '@lyearn/ui';
import { IconButtonAppearance } from '@lyearn/ui/dist/esm/Buttons/IconButton/types';
import FileInput from '@/components/FileInput';

// context
// hooks
import useAttachMediaProps from '../../hooks/useAttachMediaProps';
// types
import { ToolbarButtonProps } from '../../types';

function AttachOption({
  variant = 'ghost',
  appearance = 'secondary',
  accept = '',
  tooltipKey,
  Icon,
  uppy,
  className = 'me-4',
  editorView,
}: ToolbarButtonProps) {
  const { t } = useTranslation();
  const { inputRef, onAddAttachments } = useAttachMediaProps();

  const removeLoadingStates = useCallback(() => {
    if (editorView) {
      const { tr } = editorView.state;
      let hasEmptyImages = false;

      editorView.state.doc.descendants((node, pos) => {
        if (node.type.name === 'image' && (!node.attrs.src || node.attrs.src === '')) {
          tr.delete(pos, pos + node.nodeSize);
          hasEmptyImages = true;
        }
      });

      if (hasEmptyImages) {
        editorView.dispatch(tr);
      }
    }
  }, [editorView]);

  const addImageInEditor = useCallback(
    (url: string) => {
      if (!editorView || !editorView?.state.schema.nodes.image) return;

      const { state } = editorView;
      const imageNode = state.schema.nodes.image.create({
        src: url,
        alt: null,
        title: '',
        width: 40,
        height: null,
        displayWidth: null,
        attribution: null,
        originalSrc: null,
        disableResizing: true,
      });

      const paragraphNode = state.schema.nodes.paragraph.create();

      const insertPos = state.selection.$anchor.pos;
      const transaction = state.tr
        .insert(insertPos, imageNode)
        .insert(insertPos + 2, paragraphNode)
        .insert(insertPos + 3, paragraphNode);

      editorView.dispatch(transaction);
    },
    [editorView],
  );

  const onFileUploadSuccess = useCallback(
    (file: any) => {
      if (file.url && file.type.includes('image')) {
        addImageInEditor(file.url);
        setTimeout(() => {
          removeLoadingStates?.();
        }, 0);
      }
    },
    [addImageInEditor, removeLoadingStates],
  );

  const addImagePlaceholder = useCallback(
    (url: string) => {
      if (!editorView) return;

      const { state } = editorView;
      const { $anchor } = state.selection;

      const imageNode = state.schema.nodes.image.create({
        src: url,
        alt: null,
        title: '',
        width: 40,
        height: null,
        displayWidth: null,
        attribution: null,
        originalSrc: null,
        isUploadingExternally: true,
      });

      const insertPos = $anchor.pos;
      const transaction = state.tr.insert(insertPos, imageNode);

      editorView.dispatch(transaction);
    },
    [editorView],
  );

  if (!uppy) {
    return null;
  }

  return (
    <>
      <Tooltip placement="bottom" title={tooltipKey ? t(tooltipKey)! : ''}>
        <IconButton
          Icon={Icon}
          appearance={appearance as IconButtonAppearance}
          className={className}
          shape="square"
          size="s"
          variant={variant}
          onClick={onAddAttachments}
        />
      </Tooltip>
      <FileInput
        ref={inputRef}
        multiple
        accept={accept}
        addImagePlaceholder={addImagePlaceholder}
        uppy={uppy}
        onUploadSuccess={onFileUploadSuccess}
      />
    </>
  );
}

export default React.memo(AttachOption);
